import React from 'react';
import { convertirMonto, formatMonedaSimbolo } from '../../libs/moneda';
import { formatNumber } from '../../utils/formatNumber';
import { FUENTE_MAG, fechaLargaMag, type DiaMag } from '../../helpers/preciosMagHelper';
import type { Currency } from './CurrencyToggle';
import '../landing/brand.css';

/**
 * Precio del día en el Mercado Agroganadero de Cañuelas (fuente oficial).
 * Solo hacienda de faena: novillos, novillitos, vaquillonas, vacas, toros y MEJ.
 * Subtotal por categoría con variación contra el día anterior, y el detalle
 * por subcategoría (calidad y peso) plegado.
 */

/** "2026-09-25" → "25/09". */
const fechaCorta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const UNIT: Record<Currency, string> = {
  ARS: '$/kg',
  USD: 'u$/kg',
  constante: '$/kg',
};

const Variacion: React.FC<{ valor: number | null }> = ({ valor }) => {
  if (valor === null) return <span className="lp-note">—</span>;
  const signo = valor > 0 ? '+' : '';
  const color = valor > 0 ? 'var(--lp-pastura)' : valor < 0 ? 'var(--lp-alerta, #A8402B)' : 'var(--lp-piedra)';
  return (
    <span style={{ color, fontWeight: 600 }}>
      {signo}
      {formatNumber(valor, { decimals: 1 })}%
    </span>
  );
};

const PreciosMagHoy: React.FC<{
  dia: DiaMag;
  currency: Currency;
  usdRate: number | null;
  toggle?: React.ReactNode;
}> = ({ dia, currency, usdRate, toggle }) => {
  // Precio del día: en "constante" es el mismo valor nominal (no hay inflación que corregir).
  const moneda: Currency = currency === 'constante' ? 'ARS' : currency;
  const precio = (v: number | null) =>
    v === null ? '—' : formatMonedaSimbolo(convertirMonto(v, moneda, usdRate, null, null), moneda);

  return (
    <div className="lp-card lp-card--lift" id="hoy-mercado">
      <div className="lp-card__h">
        <h2>Hoy en el Mercado de Cañuelas</h2>
        {toggle}
      </div>
      <p className="lp-note" style={{ marginBottom: 12 }}>
        Operación del <strong>{fechaLargaMag(dia.fecha)}</strong>
        {dia.provisorio ? ' · Precios provisorios' : ''}
      </p>

      <table className="lp-scope lp-prices">
        <thead>
          <tr>
            <th scope="col">Categoría</th>
            <th scope="col">Cabezas</th>
            <th scope="col">Variación</th>
            <th scope="col">Promedio {UNIT[moneda]}</th>
          </tr>
        </thead>
        <tbody>
          {dia.categorias.map((c) => (
            <tr key={c.categoria}>
              <td>
                {c.label}
                {c.fecha !== dia.fecha && (
                  <span className="lp-note" style={{ display: 'block', fontSize: 12 }}>
                    Sin operaciones ese día · último precio del {fechaCorta(c.fecha)}
                  </span>
                )}
              </td>
              <td>{c.total?.cabezas != null ? formatNumber(c.total.cabezas) : '—'}</td>
              <td>
                <Variacion valor={c.variacion} />
              </td>
              <td>{precio(c.total?.promedio ?? null)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <details className="lp-mag-detalle" style={{ marginTop: 14 }}>
        <summary className="lp-link" style={{ cursor: 'pointer' }}>
          Ver por calidad y peso (mínimo, máximo y promedio)
        </summary>
        <div className="lp-tabla-wrap" style={{ marginTop: 12 }}>
          <table className="lp-tabla">
            <thead>
              <tr>
                <th scope="col">Categoría</th>
                <th scope="col">Mínimo</th>
                <th scope="col">Máximo</th>
                <th scope="col">Promedio</th>
                <th scope="col">Cabezas</th>
                <th scope="col">Kg prom.</th>
              </tr>
            </thead>
            <tbody>
              {dia.categorias.flatMap((c) =>
                c.subcategorias.map((s) => (
                  <tr key={`${c.categoria}-${s.subcategoria}`}>
                    <th scope="row">
                      {c.label} {s.subcategoria}
                    </th>
                    <td>{precio(s.minimo)}</td>
                    <td>{precio(s.maximo)}</td>
                    <td>{precio(s.promedio)}</td>
                    <td>{s.cabezas != null ? formatNumber(s.cabezas) : '—'}</td>
                    <td>{s.kgs_promedio ?? '—'}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
        <p className="lp-note" style={{ marginTop: 8 }}>
          "h 430" = hasta 430 kg; "+ 430" = más de 430 kg. Esp. = especial.
        </p>
      </details>

      <p className="lp-note" style={{ marginTop: 14 }}>
        Fuente oficial:{' '}
        <a href={FUENTE_MAG.url} target="_blank" rel="noopener noreferrer">
          {FUENTE_MAG.nombre}
        </a>
        . Hacienda de faena, precio por kilo vivo. La variación es contra el día anterior en que hubo precio. Los días
        sin operaciones se muestra el último precio publicado; los provisorios se ajustan en los días siguientes.
      </p>
    </div>
  );
};

export default PreciosMagHoy;
